import * as React from 'react';

import { DownOutlined } from '@ant-design/icons';

import { useFormikContext } from 'formik';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import type { TextFieldProps } from 'v2/components/Shared/FormFields/TextFormField';

type InputProps = Omit<TextFieldProps, 'form' | 'field' | 'meta'> & {
  name: string;
  select?: boolean;
  children?: React.ReactNode;
  inputProps?: Record<string, unknown>;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  minRows?: number;
};

const rootClassName = [
  '[&_label]:text-[#546E7A] [&_label]:text-base [&_label]:font-semibold [&_label]:font-sans',
  '[&_label]:h-[7px] [&_label]:[transform:translate(20px)_scale(0.75)]! [&_label]:bg-white',
  '[&_[role=button]]:border-0',
  '[&_input]:text-[#546E7A] [&_input]:p-[13px_14px] [&_input]:font-sans [&_input]:bg-white [&_input]:text-sm [&_input]:pl-5',
  '[&_input::placeholder]:text-[#78909C]',
  '[&>div>div]:text-[#546E7A] [&>div>div]:p-[13px_14px] [&>div>div]:font-sans [&>div>div]:bg-white [&>div>div]:text-sm [&>div>div]:pl-5',
  '[&>div]:bg-white',
  '[&_fieldset]:border-[#B0BEC5] [&_fieldset_legend]:ml-1.5 [&_fieldset_legend]:text-[12.8px]'
].join(' ');

const selectMenuListClassName = [
  '[&>li]:leading-[14px] [&>li]:py-2.5 [&>li]:px-4 [&>li]:font-sans',
  '[&>li:hover]:bg-[#ECEFF1]',
  '[&>li[aria-selected=true]]:bg-[#ECEFF1] [&>li[aria-selected=true]]:text-[#072D83]'
].join(' ');

function Input({ name, helperText, ...props }: InputProps) {
  const form = useFormikContext();

  return (
    <TextFormField
      id={name}
      form={form}
      field={form.getFieldProps(name)}
      meta={form.getFieldMeta(name)}
      className={rootClassName}
      helperText={typeof helperText === 'string' ? <span className='text-[#072D83]'>{helperText}</span> : helperText}
      variant='outlined'
      inputProps={{
        ['data-testid']: name
      }}
      InputLabelProps={{ shrink: true }}
      SelectProps={{
        IconComponent: () => (
          <DownOutlined className='pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-[20px] text-[#546E7A]' />
        ),
        MenuProps: {
          classes: {
            paper: 'text-[#37474F] text-sm max-h-[calc(100%-500px)]',
            list: selectMenuListClassName
          }
        }
      }}
      FormHelperTextProps={{
        className: 'mx-0 mt-2 font-sans text-[10px]'
      }}
      fullWidth
      {...props}
    />
  );
}

export { Input };
